import React, { useMemo } from 'react';
import { Button, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Lead } from '@/hooks/useLeads';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { PRIORITY_COLORS, STATUS_COLORS, isFollowUpOverdue, leadAnnualRevenue } from './leads.constants';
import { buildLeadReadiness } from '@/components/epc/commercial-ux/leadReadiness';

type Props = {
  leads: Lead[];
  isLoading?: boolean;
  epcMode?: boolean;
  onView: (lead: Lead) => void;
  onEdit: (lead: Lead) => void;
};

export const LeadsTableView: React.FC<Props> = ({ leads, isLoading, epcMode, onView, onEdit }) => {
  const { formatCurrency } = useCurrency();
  const { resolve: resolveUser } = useUserDisplayResolver();

  const columns: ColumnsType<Lead> = useMemo(
    () => [
      {
        title: 'Lead #',
        dataIndex: 'lead_number',
        key: 'lead_number',
        width: 120,
        fixed: 'left',
        render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
      },
      {
        title: 'Customer',
        dataIndex: 'customer_name',
        key: 'customer_name',
        ellipsis: true,
        sorter: (a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''),
      },
      {
        title: 'Contact',
        dataIndex: 'contact_person',
        key: 'contact_person',
        ellipsis: true,
      },
      {
        title: 'Status',
        dataIndex: 'lead_status',
        key: 'lead_status',
        width: 120,
        filters: ['New', 'Contacted', 'Qualified', 'Unqualified', 'Lost'].map((s) => ({
          text: s,
          value: s,
        })),
        onFilter: (value, record) => record.lead_status === value,
        render: (st: string) => (
          <Tag style={{ backgroundColor: STATUS_COLORS[st], color: '#fff', border: 'none', margin: 0 }}>
            {st}
          </Tag>
        ),
      },
      {
        title: 'Priority',
        dataIndex: 'priority',
        key: 'priority',
        width: 100,
        sorter: (a, b) => (a.priority || '').localeCompare(b.priority || ''),
        render: (p: string) => <Tag color={PRIORITY_COLORS[p]}>{p}</Tag>,
      },
      {
        title: 'Sales team',
        dataIndex: 'sales_team_name',
        key: 'sales_team_name',
        width: 140,
        ellipsis: true,
        render: (v: string) => v || '—',
      },
      {
        title: 'Owner',
        key: 'owner',
        width: 130,
        ellipsis: true,
        render: (_, r) => resolveUser(r.assigned_to),
      },
      {
        title: 'Annual value',
        key: 'annual_revenue',
        width: 130,
        align: 'right',
        sorter: (a, b) => leadAnnualRevenue(a) - leadAnnualRevenue(b),
        render: (_, r) => {
          const v = leadAnnualRevenue(r);
          return v > 0 ? formatCurrency(v) : '—';
        },
      },
      ...(epcMode
        ? ([
            {
              title: 'Signal',
              key: 'epc_signal',
              width: 90,
              render: (_: unknown, r: Lead) => {
                const q = buildLeadReadiness(r as unknown as Record<string, unknown>).signalQuality;
                return (
                  <Tag color={q === 'HIGH' ? 'green' : q === 'MEDIUM' ? 'blue' : 'default'}>
                    {q}
                  </Tag>
                );
              },
            },
            {
              title: 'Project value',
              key: 'epc_project_value',
              width: 130,
              align: 'right' as const,
              render: (_: unknown, r: Lead) => {
                const v = Number(r.estimated_project_value);
                return v > 0 ? formatCurrency(v) : '—';
              },
            },
          ] as ColumnsType<Lead>)
        : []),
      {
        title: 'Next follow-up',
        dataIndex: 'next_follow_up_date',
        key: 'next_follow_up_date',
        width: 130,
        sorter: (a, b) =>
          dayjs(a.next_follow_up_date || 0).valueOf() - dayjs(b.next_follow_up_date || 0).valueOf(),
        render: (d: string) => {
          if (!d) return '—';
          const overdue = isFollowUpOverdue(d);
          return (
            <Tag color={overdue ? 'error' : 'default'} style={{ margin: 0 }}>
              {dayjs(d).format('DD MMM YYYY')}
            </Tag>
          );
        },
      },
      {
        title: 'Source',
        dataIndex: 'source',
        key: 'source',
        width: 110,
        ellipsis: true,
        render: (v: string) => v || '—',
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 100,
        fixed: 'right',
        render: (_, record) => (
          <Space size="small" onClick={(e) => e.stopPropagation()}>
            <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => onView(record)} />
            <Button type="text" size="small" icon={<EditOutlined />} onClick={() => onEdit(record)} />
          </Space>
        ),
      },
    ],
    [epcMode, formatCurrency, resolveUser, onView, onEdit]
  );

  return (
    <Table<Lead>
      rowKey="id"
      loading={isLoading}
      columns={columns}
      dataSource={leads}
      scroll={{ x: 1200 }}
      pagination={{ pageSize: 25, showSizeChanger: true, showTotal: (t) => `${t} leads` }}
      onRow={(record) => ({
        onClick: () => onView(record),
        style: { cursor: 'pointer' },
      })}
      size="middle"
    />
  );
};
